import inRange from 'lodash/inRange';
import raven from 'raven-js';

import ApiClient, { LearningRecapResponse } from '../../../helpers/ApiClient';
import { Exercise } from '../../../helpers/fetchExternalExerciseIds';
import { formatDistanceLocalized } from '../../../helpers/formatDistanceLocalized';
import { getCampusApiClient } from '../../../helpers/getClients';
import { getLanguage } from '../../../i18n';
import { REQUEST_STATUS } from '../../selectors';

export type LearningRecap = {
  chapterNumber: number;
  chapterTitle: string;
  courseTitle: string;
  lastActive: string;
  lastActiveDate: Date;
  nextLessonGoal: string;
  summary: string;
};

export const getLearningRecapAsPromise = async ({
  chapterAsPromise,
  courseAsPromise,
  exerciseNumber,
  exercisesAsPromise,
  isPreview,
  language,
  preFetchedData,
}: {
  chapterAsPromise: Promise<{ number: number; title: string }>;
  courseAsPromise: Promise<{ id: number; title: string }>;
  exerciseNumber: number;
  exercisesAsPromise: Promise<Exercise[]>;
  isPreview: boolean;
  language: string;
  preFetchedData: any;
}): Promise<LearningRecap | null> => {
  const preFetchedDataRequest = preFetchedData.get('learningRecap');
  switch (preFetchedDataRequest.get('status')) {
    case REQUEST_STATUS.SUCCESS:
      return Promise.resolve(preFetchedDataRequest.get('data'));
    case REQUEST_STATUS.FAILED:
      return Promise.reject(preFetchedDataRequest.get('data'));
    default: {
      const course = await courseAsPromise;
      const client = getCampusApiClient();

      if (isPreview) {
        return Promise.resolve(null);
      }

      const exercises = await exercisesAsPromise;
      let nextExerciseNumber: number = exerciseNumber;
      if (!inRange(exerciseNumber - 1, exercises.length)) {
        nextExerciseNumber = 1;
      }

      const nextExerciseId = exercises.find(
        (ex) => ex.number === nextExerciseNumber,
      )?.id;

      if (nextExerciseId === undefined) {
        return Promise.resolve(null);
      }

      const chapter = await chapterAsPromise;

      return client
        .execute(
          ApiClient.endpoint.getCourseLearningRecap({
            courseId: course.id,
            nextExerciseId,
            language: getLanguage(language),
          }),
        )
        .toPromise()
        .then(({ body }: { body: unknown }): LearningRecap | null => {
          const data = LearningRecapResponse.parse(body);
          if (data === null) {
            return null;
          }

          const now = new Date();
          const lastActiveDate = new Date(data.lastActiveDate);
          const lastActive = formatDistanceLocalized(
            lastActiveDate,
            now,
            language,
          );

          return {
            summary: data.summary,
            nextLessonGoal: data.nextLessonGoal,
            lastActive,
            chapterNumber: chapter.number,
            chapterTitle: chapter.title,
            courseTitle: course.title,
            lastActiveDate,
          };
        })
        .catch((error: any) => {
          const status = error?.error?.status ?? 0;
          const isError = status >= 500;

          if (isError) {
            raven.captureMessage(
              `Error handling learning recap ${error?.error?.message ?? ''}`,
              {
                extra: {
                  error,
                  course_id: course.id,
                  next_exercise_id: nextExerciseId,
                  status,
                },
              },
            );
          }

          return Promise.resolve(null);
        });
    }
  }
};
